import { perceptionClient } from "@perception/perceptionClient";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  type ClinicMap,
  clinicMapClient,
  type DoorAreasChange,
  type MapDocument,
} from "./clinicMapClient";
import { doorLabels } from "./mapWords";

const clinicMapKeys = {
  map: (sessionKey: string) => ["clinic-map", sessionKey] as const,
  doorAreas: (sessionKey: string, roomId: string) =>
    ["clinic-map", sessionKey, "door-areas", roomId] as const,
  heldStill: (sessionKey: string, roomId: string) =>
    ["clinic-map", sessionKey, "held-still", roomId] as const,
  live: (sessionKey: string) => ["clinic-map", sessionKey, "live"] as const,
};

/** A room re-reads its camera about every 2 s, so a faster poll shows nothing new. */
export const LIVE_MAP_POLL_MS = 3_000;

/** Until a map is saved there is nothing to draw, so it checks only now and then. */
const NO_MAP_POLL_MS = 60_000;

export const useLiveClinicMap = (sessionKey: string) =>
  useQuery({
    queryKey: clinicMapKeys.live(sessionKey),
    queryFn: ({ signal }) => clinicMapClient.live(signal),
    refetchInterval: (query) =>
      query.state.data?.revision === 0 ? NO_MAP_POLL_MS : LIVE_MAP_POLL_MS,
  });

/** The editor's map lists every display, so a display change reads it again. */
export const clinicMapQueryKey = (sessionKey: string) =>
  clinicMapKeys.map(sessionKey);

export const useClinicMap = (sessionKey: string, enabled = true) =>
  useQuery({
    queryKey: clinicMapKeys.map(sessionKey),
    queryFn: ({ signal }) => clinicMapClient.get(signal),
    enabled,
  });

/**
 * The room's doors on the saved map and where each leads (`doorLabels`), empty
 * when the room is not on it; undefined until the map and rooms load. Every
 * room, not only active ones, so a door to a room out of service keeps its name.
 */
export const useRoomDoorLabels = (
  sessionKey: string,
  roomId: string,
  enabled: boolean,
) => {
  const map = useClinicMap(sessionKey, enabled);
  const rooms = useRoomsListQuery(sessionKey, undefined, enabled);
  const labels = useMemo(() => {
    if (!map.data || !rooms.data) return undefined;
    const doc = map.data.document;
    const space = doc.spaces.find((s) => s.room_uuid === roomId);
    if (!space) return new Map<string, string>();
    return doorLabels(doc, space, new Map(rooms.data.map((r) => [r.id, r])));
  }, [map.data, rooms.data, roomId]);
  return { labels, refetch: map.refetch };
};

/** The saved map comes back whole, warnings included, so it replaces the cache. */
export const useSaveClinicMap = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      expectedRevision,
      document,
    }: {
      expectedRevision: number;
      document: MapDocument;
    }) => clinicMapClient.save(expectedRevision, document),
    onSuccess: (saved: ClinicMap) =>
      queryClient.setQueryData(clinicMapKeys.map(sessionKey), saved),
    meta: { handled: true },
  });
};

export const useRoomDoorAreas = (sessionKey: string, roomId: string) =>
  useQuery({
    queryKey: clinicMapKeys.doorAreas(sessionKey, roomId),
    queryFn: () => clinicMapClient.doorAreas(roomId),
  });

/** One still held while doors are drawn on it, unlike the Perception tab's polled one. */
export const useHeldStill = (sessionKey: string, roomId: string) =>
  useQuery({
    queryKey: clinicMapKeys.heldStill(sessionKey, roomId),
    queryFn: () => perceptionClient.still(roomId),
    staleTime: Infinity,
    gcTime: 0,
  });

/** The map's warnings name rooms with no door areas, so the map is read again. */
export const useSetRoomDoorAreas = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomUuid,
      change,
    }: {
      roomUuid: string;
      change: DoorAreasChange;
    }) => clinicMapClient.setDoorAreas(roomUuid, change),
    onSuccess: (areas, { roomUuid }) => {
      queryClient.setQueryData(
        clinicMapKeys.doorAreas(sessionKey, roomUuid),
        areas,
      );
      void queryClient.invalidateQueries({
        queryKey: clinicMapKeys.map(sessionKey),
        exact: true,
      });
    },
    meta: { handled: true },
  });
};
